import { useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';

// Different celebration types for variety
const celebrationTypes = [
  'confetti',
  'fireworks',
  'stars',
  'emoji',
  'rainbow'
];

// Get a random celebration type
const getRandomCelebration = () => {
  return celebrationTypes[Math.floor(Math.random() * celebrationTypes.length)];
};

// Confetti burst
const fireConfetti = () => {
  const count = 200;
  const defaults = {
    origin: { y: 0.7 },
    zIndex: 9999
  };

  function fire(particleRatio, opts) {
    confetti({
      ...defaults,
      ...opts,
      particleCount: Math.floor(count * particleRatio)
    });
  }

  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

// Fireworks effect — bursts colored radial explosions high in the "sky".
// Each burst uses a SINGLE bright color (like a real firework) instead of
// mixed-color confetti, so it's visually distinct from the standard
// `fireConfetti` (which is multi-color and grounded near the bottom).
const fireFireworks = () => {
  const duration = 3000;
  const animationEnd = Date.now() + duration;
  const palette = [
    ['#EFB342', '#FFD700', '#FFF6C7'],  // gold burst
    ['#FF6B6B', '#FF1744', '#FFCDD2'],  // red burst
    ['#4FC3F7', '#2196F3', '#BBDEFB'],  // blue burst
    ['#69F0AE', '#00E676', '#B9F6CA'],  // green burst
    ['#E040FB', '#D500F9', '#F8BBD0'],  // magenta burst
    ['#FFAB40', '#FF6D00', '#FFE0B2'],  // orange burst
  ];

  let burstIndex = 0;
  const interval = setInterval(() => {
    if (Date.now() >= animationEnd) return clearInterval(interval);

    const colors = palette[burstIndex % palette.length];
    burstIndex += 1;
    // Random launch spot, but always in the upper half of the screen.
    const origin = { x: 0.15 + Math.random() * 0.7, y: 0.15 + Math.random() * 0.3 };

    // Main radial explosion — single color, high velocity, no gravity trail.
    confetti({
      particleCount: 80,
      startVelocity: 45,
      spread: 360,
      ticks: 90,
      gravity: 0.6,
      decay: 0.92,
      scalar: 1,
      colors,
      origin,
      zIndex: 9999,
    });
    // A smaller inner ring of sparks for depth.
    confetti({
      particleCount: 30,
      startVelocity: 20,
      spread: 360,
      ticks: 60,
      gravity: 0.4,
      decay: 0.9,
      scalar: 0.6,
      shapes: ['circle'],
      colors,
      origin,
      zIndex: 9999,
    });
  }, 400);
};

// Stars effect
const fireStars = () => {
  const defaults = {
    spread: 360,
    ticks: 100,
    gravity: 0,
    decay: 0.94,
    startVelocity: 30,
    shapes: ['star'],
    colors: ['#EFB342', '#FFD700', '#FFA500', '#FF6B6B', '#4ECDC4'],
    zIndex: 9999
  };

  function shoot() {
    confetti({
      ...defaults,
      particleCount: 40,
      scalar: 1.2,
      shapes: ['star']
    });

    confetti({
      ...defaults,
      particleCount: 10,
      scalar: 0.75,
      shapes: ['circle']
    });
  }

  setTimeout(shoot, 0);
  setTimeout(shoot, 100);
  setTimeout(shoot, 200);
};

// Emoji celebration
const fireEmoji = () => {
  const emojis = ['🎉', '🏆', '⭐', '💪', '🔥', '👏', '🎊', '✨'];
  const scalar = 2;
  
  emojis.forEach((emoji, i) => {
    setTimeout(() => {
      confetti({
        particleCount: 1,
        startVelocity: 0,
        ticks: 200,
        gravity: 0.5,
        origin: {
          x: Math.random(),
          y: Math.random() * 0.3
        },
        shapes: ['text'],
        shapeOptions: {
          text: {
            value: emoji
          }
        },
        scalar,
        zIndex: 9999
      });
    }, i * 50);
  });
  
  // Also fire some confetti
  setTimeout(() => fireConfetti(), 100);
};

// Rainbow effect
const fireRainbow = () => {
  const colors = ['#ff0000', '#ff7f00', '#ffff00', '#00ff00', '#0000ff', '#4b0082', '#9400d3'];
  
  colors.forEach((color, i) => {
    setTimeout(() => {
      confetti({
        particleCount: 40,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: [color],
        zIndex: 9999
      });
      confetti({
        particleCount: 40,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: [color],
        zIndex: 9999
      });
    }, i * 100);
  });
};

// Main celebration trigger function
export const triggerCelebration = (type = null) => {
  const celebrationType = type || getRandomCelebration();
  
  switch (celebrationType) {
    case 'confetti':
      fireConfetti();
      break;
    case 'fireworks':
      fireFireworks();
      break;
    case 'stars':
      fireStars();
      break;
    case 'emoji':
      fireEmoji();
      break;
    case 'rainbow':
      fireRainbow();
      break;
    default:
      fireConfetti();
  }
};

// Hook to trigger celebration and show badge modal
export const useCelebration = () => {
  const celebrate = useCallback((type = null) => {
    triggerCelebration(type);
  }, []);

  return { celebrate, triggerCelebration };
};

// Component that listens for celebration events
const Celebration = () => {
  useEffect(() => {
    const handleCelebration = (event) => {
      const type = event.detail?.type || null;
      triggerCelebration(type);
    };

    window.addEventListener('celebrate', handleCelebration);
    return () => window.removeEventListener('celebrate', handleCelebration);
  }, []);

  return null; // This component doesn't render anything visible
};

// Utility to dispatch celebration event from anywhere
export const dispatchCelebration = (type = null) => {
  window.dispatchEvent(new CustomEvent('celebrate', { detail: { type } }));
};

export default Celebration;
